import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import AppLayout from './components/AppLayout.tsx';
import Home from './pages/Home.tsx';
import StorySetup from './pages/StorySetup.tsx';
import CharacterBuilder from './pages/CharacterBuilder.tsx';
import Characters from './pages/Characters.tsx';
import ScenesLibrary from './pages/ScenesLibrary.tsx';
import VideosLibrary from './pages/VideosLibrary.tsx';
import SceneBuilder from './pages/SceneBuilder.tsx';
import VideoBuilder from './pages/VideoBuilder.tsx';
import VideoEditor from './pages/VideoEditor.tsx';
import FinalStory from './pages/FinalStory.tsx';
import Login from './pages/Login.tsx';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// Protected routes component
const ProtectedRoutes: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Register page disabled for now - redirect to login
    if (window.location.pathname === '/register') {
      return <Navigate to="/login" replace />;
    }
    return <Login />;
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="/home" element={<Home />} />
      <Route path="/story-setup" element={<StorySetup />} />
      <Route path="/character-builder" element={
        <AppLayout>
          <CharacterBuilder />
        </AppLayout>
      } />
      <Route path="/characters" element={<Characters />} />
      <Route path="/scenes" element={<ScenesLibrary />} />
      <Route path="/videos" element={<VideosLibrary />} />
      <Route path="/character-builder/:storyId" element={
        <AppLayout>
          <CharacterBuilder />
        </AppLayout>
      } />
      <Route path="/scene-builder" element={
        <AppLayout>
          <SceneBuilder />
        </AppLayout>
      } />
      <Route path="/scenes" element={
        <AppLayout>
          <SceneBuilder />
        </AppLayout>
      } />
      <Route path="/scenes/:storyId" element={
        <AppLayout>
          <SceneBuilder />
        </AppLayout>
      } />
      <Route path="/videos" element={
        <AppLayout>
          <VideoBuilder />
        </AppLayout>
      } />
      <Route path="/videos/:storyId" element={
        <AppLayout>
          <VideoBuilder />
        </AppLayout>
      } />
      <Route path="/video-editor" element={<VideoEditor />} />
      <Route path="/final-story" element={
        <AppLayout>
          <FinalStory />
        </AppLayout>
      } />
      <Route path="/final-story/:storyId" element={
        <AppLayout>
          <FinalStory />
        </AppLayout>
      } />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <div className="App">
            <ProtectedRoutes />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
